import React, { useState, useRef, useCallback } from 'react';
import { Upload, FileText, AlertCircle, CheckCircle, X } from 'lucide-react';

interface UploadFile {
  file: File;
  id: string;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  progress: number;
  error?: string;
}

interface MedicalReviewUploadZoneProps {
  onFilesUpload?: (files: File[]) => Promise<void>;
  className?: string;
}

const ALLOWED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/jpg', 
  'image/png',
  'image/tiff',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
];

const ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.tiff', '.docx'];
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

export const MedicalReviewUploadZone: React.FC<MedicalReviewUploadZoneProps> = ({
  onFilesUpload,
  className = ''
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<UploadFile[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);

  const validateFile = (file: File): string | null => {
    // Check file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      const extension = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(extension)) {
        return `File type not supported. Allowed types: PDF, JPG, PNG, TIFF, DOCX`;
      }
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return `File size exceeds 50MB limit. File is ${(file.size / 1024 / 1024).toFixed(1)}MB`;
    }

    return null;
  };

  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files) return;

    const fileArray = Array.from(files);
    const newUploadFiles: UploadFile[] = [];

    for (const file of fileArray) {
      const validation = validateFile(file);
      newUploadFiles.push({
        file,
        id: `${file.name}-${Date.now()}-${Math.random()}`,
        status: validation ? 'error' : 'pending',
        progress: 0,
        error: validation || undefined
      });
    }

    setUploadFiles(prev => [...prev, ...newUploadFiles]);
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    console.log('🚨 DRAG ENTER EVENT FIRED!', e);
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current <= 0) {
      setIsDragOver(false);
      dragCounterRef.current = 0;
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    console.log('🚨 DROP EVENT FIRED!', e, e.dataTransfer.files);
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    dragCounterRef.current = 0;

    const files = e.dataTransfer.files;
    handleFileSelect(files);
  }, [handleFileSelect]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
    // Reset input value so same file can be selected again
    e.target.value = '';
  }, [handleFileSelect]);

  const handleRemoveFile = useCallback((fileId: string) => {
    setUploadFiles(prev => prev.filter(f => f.id !== fileId));
  }, []);

  const handleStartUpload = useCallback(async () => {
    const validFiles = uploadFiles.filter(f => f.status === 'pending');
    if (validFiles.length === 0) return;

    setIsProcessing(true);
    
    try {
      // Mark files as uploading
      setUploadFiles(prev => prev.map(f => 
        f.status === 'pending' ? { ...f, status: 'uploading' as const } : f
      ));

      // Simulate upload progress (in real implementation, this would track actual upload)
      const files = validFiles.map(f => f.file);
      
      // Call the upload handler if provided
      if (onFilesUpload) {
        await onFilesUpload(files);
      }

      // Mark all as completed
      setUploadFiles(prev => prev.map(f => 
        f.status === 'uploading' ? { ...f, status: 'completed' as const, progress: 100 } : f
      ));

    } catch (error) {
      console.error('Upload failed:', error);
      setUploadFiles(prev => prev.map(f => 
        f.status === 'uploading' ? { 
          ...f, 
          status: 'error' as const, 
          error: 'Upload failed. Please try again.' 
        } : f
      ));
    } finally {
      setIsProcessing(false);
    }
  }, [uploadFiles, onFilesUpload]);

  const handleClearAll = useCallback(() => {
    setUploadFiles([]);
  }, []);

  const validFileCount = uploadFiles.filter(f => f.status === 'pending' || f.status === 'completed').length;
  const errorFileCount = uploadFiles.filter(f => f.status === 'error').length;

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Upload Zone */}
      <div
        className={`
          relative border-2 border-dashed rounded-lg p-8 text-center transition-colors
          ${isDragOver 
            ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20' 
            : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
          }
          ${isProcessing ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}
        `}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => !isProcessing && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ALLOWED_EXTENSIONS.join(',')}
          onChange={handleFileInputChange}
          className="hidden"
          disabled={isProcessing}
        />

        <div className="space-y-4">
          <div className="flex justify-center">
            <Upload className={`w-12 h-12 ${isDragOver ? 'text-blue-500' : 'text-gray-400'}`} />
          </div>

          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Upload Medical Records
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {isDragOver 
                ? 'Drop files here to upload' 
                : 'Drag and drop files here, or click to browse'
              }
            </p>
          </div>

          <div className="text-xs text-gray-400 space-y-1">
            <p>Supported formats: PDF, JPG, PNG, TIFF, DOCX</p>
            <p>Maximum file size: 50MB per file</p>
          </div>
        </div>
      </div>

      {/* File List */}
      {uploadFiles.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white">
              Files ({uploadFiles.length})
            </h4>
            <div className="flex space-x-2">
              {validFileCount > 0 && !isProcessing && (
                <button
                  onClick={handleStartUpload}
                  className="px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
                  disabled={validFileCount === 0}
                >
                  Start Upload ({validFileCount})
                </button>
              )}
              <button
                onClick={handleClearAll}
                className="px-3 py-1 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
                disabled={isProcessing}
              >
                Clear All
              </button>
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto space-y-2">
            {uploadFiles.map((uploadFile) => (
              <div
                key={uploadFile.id}
                className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-md"
              >
                <div className="flex-shrink-0">
                  {uploadFile.status === 'error' ? (
                    <AlertCircle className="w-5 h-5 text-red-500" />
                  ) : uploadFile.status === 'completed' ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <FileText className="w-5 h-5 text-gray-400" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {uploadFile.file.name}
                    </p>
                    <button
                      onClick={() => handleRemoveFile(uploadFile.id)}
                      className="flex-shrink-0 text-gray-400 hover:text-red-500"
                      disabled={uploadFile.status === 'uploading'}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="flex items-center space-x-2 mt-1">
                    <p className="text-xs text-gray-500">
                      {(uploadFile.file.size / 1024 / 1024).toFixed(1)} MB
                    </p>
                    <span className={`
                      text-xs px-2 py-0.5 rounded-full
                      ${uploadFile.status === 'error' ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400' : ''}
                      ${uploadFile.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' : ''}
                      ${uploadFile.status === 'pending' ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' : ''}
                      ${uploadFile.status === 'uploading' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400' : ''}
                    `}>
                      {uploadFile.status === 'error' ? 'Error' : ''}
                      {uploadFile.status === 'completed' ? 'Uploaded' : ''}
                      {uploadFile.status === 'pending' ? 'Ready' : ''}
                      {uploadFile.status === 'uploading' ? 'Uploading...' : ''}
                    </span>
                  </div>

                  {uploadFile.error && (
                    <p className="text-xs text-red-500 mt-1">
                      {uploadFile.error}
                    </p>
                  )}

                  {uploadFile.status === 'uploading' && (
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 mt-2">
                      <div 
                        className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                        style={{ width: `${uploadFile.progress}%` }}
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Summary */}
          {(validFileCount > 0 || errorFileCount > 0) && (
            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 pt-2 border-t border-gray-200 dark:border-gray-700">
              <span>
                {validFileCount > 0 && `${validFileCount} valid file${validFileCount !== 1 ? 's' : ''}`}
                {validFileCount > 0 && errorFileCount > 0 && ' • '}
                {errorFileCount > 0 && `${errorFileCount} error${errorFileCount !== 1 ? 's' : ''}`}
              </span>
              <span>
                Total size: {(uploadFiles.reduce((sum, f) => sum + f.file.size, 0) / 1024 / 1024).toFixed(1)} MB
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};